import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Company, type FilingDetails, type TaxOffice } from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from '../components/Icons';
import { TaxOfficePicker } from '../components/TaxOfficePicker';

const empty: FilingDetails = {
  taxOfficeCode: null,
  firstName: null,
  lastName: null,
  birthDate: null,
  email: null,
  phone: null,
  zusInsuranceTitleCode: null,
};

/**
 * Dane podatnika potrzebne do plików JPK i deklaracji (E8-S2).
 *
 * Osobny formularz, bo te dane służą jednej rzeczy — wysyłce do urzędu. Jednoosobowa
 * działalność przedstawia się w JPK imieniem, nazwiskiem i datą urodzenia; spółka samą
 * nazwą, więc pól osobowych jej nie pokazujemy.
 */
export function FilingDetailsPanel({ company, t }: { company: Company; t: Dictionary }) {
  const [form, setForm] = useState<FilingDetails | null>(null);
  /* Słownik urzędów: czterysta wierszy, raz na wejście na ekran; filtrowanie jest w przeglądarce */
  const [offices, setOffices] = useState<TaxOffice[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const person = company.legalForm === 'JDG';

  const load = useCallback(async () => {
    try {
      const [details, dictionary] = await Promise.all([api.filingDetails(company.id), api.taxOffices()]);
      setForm(details);
      setOffices(dictionary);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
      setForm(empty);
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      setForm(await api.saveFilingDetails(company.id, form));
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  if (!form) return <p className="muted">{t.loading}</p>;

  /** Puste pole zapisujemy jako brak wartości, a nie jako pusty napis. */
  const change = (field: keyof FilingDetails) => (value: string) =>
    setForm({ ...form, [field]: value.trim() === '' ? null : value });

  return (
    <div className="panel">
      <header>
        <h4>{t.filing.title}</h4>
      </header>

      {/* Te dane służą jednej rzeczy — wysyłce do urzędu; do faktury nie są używane */}
      <div className="alert a-info">
        <Icon.Info />
        <div>
          <b>{t.filing.lead}</b>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.filing.saved}</b>
          </div>
        </div>
      )}

      <form onSubmit={(event) => void save(event)}>
        <div className="fields">
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="fTaxOffice">{t.filing.taxOffice}</label>
            <TaxOfficePicker
              id="fTaxOffice"
              offices={offices}
              value={form.taxOfficeCode}
              city={company.city}
              t={t}
              onChange={(code) => setForm({ ...form, taxOfficeCode: code })}
            />
            {/* Urząd dla największych podatników w małej firmie to niemal na pewno pomyłka */}
            {offices.find((office) => office.code === form.taxOfficeCode)?.specialised ? (
              <span className="hint warn">{t.filing.taxOfficeSpecialisedHint}</span>
            ) : (
              <span className="hint">{t.filing.taxOfficeHint}</span>
            )}
          </div>
          <div className="field">
            <label htmlFor="fEmail">{t.filing.email}</label>
            <input
              id="fEmail"
              className="inp"
              type="email"
              value={form.email ?? ''}
              onChange={(event) => change('email')(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="fPhone">{t.filing.phone}</label>
            <input
              id="fPhone"
              className="inp mono"
              value={form.phone ?? ''}
              onChange={(event) => change('phone')(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="fZusTitle">{t.filing.zusInsuranceTitleCode}</label>
            <input
              id="fZusTitle"
              className="inp mono"
              inputMode="numeric"
              maxLength={6}
              value={form.zusInsuranceTitleCode ?? ''}
              onChange={(event) => change('zusInsuranceTitleCode')(event.target.value)}
            />
            {/*
              Kodu nie podstawiamy sami: słownik ZUS-u jest jednoznaczny tylko przy pełnych
              i preferencyjnych składkach, a wpisany na chybił trafił przypisałby deklarację
              do niewłaściwego tytułu ubezpieczenia — czego nie widać ani w pliku, ani na
              potwierdzeniu z ZUS-u.
            */}
            <span className="hint">{t.filing.zusInsuranceTitleCodeHint}</span>
          </div>
          {person && (
            <>
              <div className="field">
                <label htmlFor="fFirst">{t.filing.firstName}</label>
                <input
                  id="fFirst"
                  className="inp"
                  value={form.firstName ?? ''}
                  onChange={(event) => change('firstName')(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="fLast">{t.filing.lastName}</label>
                <input
                  id="fLast"
                  className="inp"
                  value={form.lastName ?? ''}
                  onChange={(event) => change('lastName')(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="fBirth">{t.filing.birthDate}</label>
                <input
                  id="fBirth"
                  className="inp"
                  type="date"
                  value={form.birthDate ?? ''}
                  onChange={(event) => change('birthDate')(event.target.value)}
                />
              </div>
            </>
          )}
        </div>
        {person && <p className="hint">{t.filing.personHint}</p>}
        <div className="bar2">
          <button type="submit" className="btn" disabled={saving}>
            {saving ? t.companies.saving : t.filing.save}
          </button>
        </div>
      </form>
    </div>
  );
}
